import { v3 } from '../math.ts';
import { box, type LevelGeometry, type Torch } from '../level.ts';
import { makePool, makeShrine, type Arena } from '../arena.ts';
import { VALHALLA_WALK } from '../constants.ts';

/**
 * Great Cathedral — generated from a Godot blockout. Do not hand-edit.
 *
 * Rebuild with `python3 tools/godot_arena.py`. Anything written here by hand
 * is lost the next time the level is exported, so tuning belongs either in
 * the .tscn (shape) or in the asset manifest (looks).
 *
 * 307 boxes, 61 torches, 8 pools.
 * Godot lays the arena out in the positive quadrant; core centres it, so every
 * coordinate below is shifted by half the 77x72 floor.
 */

/** Valhalla is dug well below the arena and reachable only by dying. */
const VY = -40;
const VH = 6;
const VHALFW = 5;

function valhallaRoom(): ReturnType<typeof box>[] {
  const half = VALHALLA_WALK / 2 + 4;
  return [
    box(0, VY - 0.5, 0, VHALFW * 2 + 2, 1, half * 2, 'floor', 'valhalla'),
    box(0, VY + VH, 0, VHALFW * 2 + 2, 1, half * 2, 'ceiling', 'valhalla'),
    box(-VHALFW, VY + VH / 2, 0, 1, VH, half * 2, 'wall', 'valhalla'),
    box(VHALFW, VY + VH / 2, 0, 1, VH, half * 2, 'wall', 'valhalla'),
    box(0, VY + VH / 2, -half, VHALFW * 2, VH, 1, 'wall', 'valhalla'),
    box(0, VY + VH / 2, half, VHALFW * 2, VH, 1, 'wall', 'valhalla'),
  ];
}

/**
 * Torches down the Valhalla corridor.
 *
 * The corridor is dug forty metres below the arena and no blockout will ever
 * place fixtures in it, because it is not a room anybody draws — it is
 * generated. Four brackets, two a side and staggered so the walk is lit
 * unevenly rather than as a runway, which is enough to see the far end by
 * without making the place pleasant. It is a punishment.
 */
function valhallaTorches(): Torch[] {
  const reach = VALHALLA_WALK / 2;
  const HALF_TURN = Math.PI;
  return [
    // Facing inward: yaw zero looks along -Z, so a torch on the west wall
    // turns a quarter to point east across the corridor.
    { pos: v3(-VHALFW + 0.6, VY + 2.6, -reach * 0.55), yaw: HALF_TURN / 2 },
    { pos: v3(VHALFW - 0.6, VY + 2.6, -reach * 0.15), yaw: -HALF_TURN / 2 },
    { pos: v3(-VHALFW + 0.6, VY + 2.6, reach * 0.25), yaw: HALF_TURN / 2 },
    { pos: v3(VHALFW - 0.6, VY + 2.6, reach * 0.65), yaw: -HALF_TURN / 2 },
  ];
}

export const GREAT_CATHEDRAL_GEOMETRY: LevelGeometry = {
  name: 'Great Cathedral',
  /*
   * Low, so that the torches have something to be brighter than.
   *
   * It sat at 0.85 while the arena had fourteen torches in one corner and
   * needed to be legible anyway. With a hundred fixtures placed, a high
   * ambient is the thing *stopping* them reading: light only looks like
   * light against dark, and at 0.85 there was no dark anywhere.
   */
  ambient: 0.38,
  /*
   * A torch lights its corner, not the building.
   *
   * The range was 34m, which is most of a room and reaches well into the
   * next one — so fourteen lit fixtures overlapped into a single flat
   * wash the colour of fire, and no individual torch was visible as a
   * source of anything. Eight metres is a pool of light you can stand in
   * and step out of, which is what makes a corridor of them read as a
   * corridor of them.
   */
  torchIntensity: 20,
  torchRange: 10,
  boxes: [
    ...valhallaRoom(),
    box(-30.049, -0.25, -29.995, 12.314, 0.5, 12.335, 'floor', 'Floor_ChaosShrine'),
    box(-30.012, -0.25, -22.438, 2.313, 0.5, 2.786, 'floor', 'Floor_ChaosShrine_Doorway'),
    box(-21.231, -0.25, -32.488, 5.338, 0.5, 2.359, 'floor', 'Floor_ChaosShrine_Corridor'),
    box(-33.814, -0.25, -18.554, 9.696, 0.5, 4.987, 'floor', 'Floor_ChaosPool01'),
    box(-31.344, -0.25, -12.449, 9.966, 0.5, 7.242, 'floor', 'Floor_ChaosPool02'),
    box(-31.607, -0.25, -1.344, 3.954, 0.5, 14.992, 'floor', 'Floor_ChaosPool_Gallery'),
    box(-13.756, -0.25, -27.414, 9.657, 0.5, 12.272, 'floor', 'Floor_Dungeon'),
    box(-3.763, -0.25, -22.473, 10.354, 0.5, 2.383, 'floor', 'Floor_Dungeon_Corridor'),
    box(-18.652, -0.25, -11.242, 15.434, 0.5, 4.796, 'floor', 'Floor_Library_Gallery'),
    box(-9.777, -0.25, -10.032, 2.346, 0.5, 2.591, 'floor', 'Floor_Library_Doorway_01'),
    box(4.96, -0.25, -19.935, 2.247, 0.5, 2.809, 'floor', 'Floor_Library_Doorway_02'),
    box(-0.046, -0.25, -13.584, 17.16, 0.5, 9.903, 'floor', 'Floor_Library'),
    box(10.513, -0.25, -14.935, 3.972, 0.5, 2.153, 'floor', 'Floor_Library_Corridor'),
    box(4.967, -0.25, -24.935, 7.133, 0.5, 7.213, 'floor', 'Floor_Greenhouse01'),
    box(11.211, -0.25, -27.342, 5.365, 0.5, 2.372, 'floor', 'Floor_Greenhouse02'),
    box(18.768, -0.25, -26.18, 9.796, 0.5, 9.653, 'floor', 'Floor_Greenhouse03'),
    box(19.971, -0.25, -18.684, 2.156, 0.5, 5.371, 'floor', 'Floor_Greenhouse_Corridor'),
    box(18.037, -0.25, -9.298, 11.179, 0.5, 13.405, 'floor', 'Floor_Tabernacle'),
    box(19.971, -0.25, 0.691, 2.156, 0.5, 6.647, 'floor', 'Floor_Tabernacle_Corridor'),
    box(25.01, -0.25, -14.935, 2.775, 0.5, 2.153, 'floor', 'Floor_OrderShrine_Corridor'),
    box(32.479, -0.25, -19.975, 12.163, 0.5, 12.195, 'floor', 'Floor_OrderShrine'),
    box(34.803, -0.25, -13.704, 2.192, 0.5, 0.359, 'floor', 'Floor_OrderShrine_Doorway'),
    box(34.964, -0.25, -6.187, 7.331, 0.5, 14.69, 'floor', 'Floor_OrderPool'),
    box(34.962, -0.25, 1.326, 2.118, 0.5, 0.342, 'floor', 'Floor_OrderPool_Doorway'),
    box(30.004, -0.25, 11.283, 17.318, 0.5, 14.661, 'floor', 'Floor_Cloister01'),
    box(34.968, -0.25, 2.736, 2.142, 0.5, 2.479, 'floor', 'Floor_Cloister02'),
    box(27.477, -0.25, 20.057, 2.218, 0.5, 2.938, 'floor', 'Floor_Cloister_Doorway_01'),
    box(21.189, -0.25, 10.073, 0.319, 0.5, 2.158, 'floor', 'Floor_Cloister_Doorway_02'),
    box(1.403, -0.25, 11.302, 35.069, 0.5, 14.624, 'floor', 'Floor_Chapel01'),
    box(19.99, -0.25, 7.566, 2.118, 0.5, 7.103, 'floor', 'Floor_Chapel02'),
    box(27.646, -0.25, 26.642, 12.602, 0.5, 10.277, 'floor', 'Floor_Diningroom'),
    box(16.189, -0.25, 26.95, 10.333, 0.5, 5.99, 'floor', 'Floor_Kitchen_Gallery'),
    box(6.478, -0.25, 27.565, 9.13, 0.5, 12.237, 'floor', 'Floor_Kitchen'),
    box(7.462, -0.25, 20.029, 2.181, 0.5, 2.851, 'floor', 'Floor_Kitchen_Doorway'),
    box(-9.027, -0.25, -1.243, 11.347, 0.5, 9.765, 'floor', 'Floor_Study'),
    box(-12.768, -0.25, -7.478, 3.65, 0.5, 2.733, 'floor', 'Floor_Study_Corridor'),
    box(-4.972, -0.25, 3.814, 3.131, 0.5, 0.364, 'floor', 'Floor_Study_Doorway'),
    box(-28.742, -0.25, 5.061, 1.828, 0.5, 2.137, 'floor', 'Floor_Storage_Doorway_01'),
    box(-17.485, -0.25, 7.554, 2.736, 0.5, 2.128, 'floor', 'Floor_Storage_Doorway_02'),
    box(-23.33, -0.25, 8.73, 9.015, 0.5, 9.917, 'floor', 'Floor_Storage'),
    box(-25.905, -0.25, 18.815, 3.864, 0.5, 10.256, 'floor', 'Floor_Storage_Gallery'),
    box(-26.327, -0.25, 30.047, 9.75, 0.5, 12.231, 'floor', 'Floor_BalancePool'),
    box(-12.355, -0.25, 30.572, 11.898, 0.5, 10.849, 'floor', 'Floor_BalanceShrine'),
    box(-2.369, -0.25, 31.325, 8.132, 0.5, 4.642, 'floor', 'Floor_BalanceShrine_Gallery'),
    box(-7.518, -0.25, 21.892, 2.214, 0.5, 6.558, 'floor', 'Floor_BalanceShrine_Corridor_01'),
    box(-19.878, -0.25, 32.547, 3.181, 0.5, 2.14, 'floor', 'Floor_BalanceShrine_Corridor_02'),
    box(1.921, -0.25, 32.507, 0.463, 0.5, 2.217, 'floor', 'Floor_BalanceShrine_Doorway'),
    box(-30.227, 3.0, -36.194, 12.501, 6.0, 0.4, 'wall', 'WallH_1'),
    box(-16.26, 3.0, -33.694, 14.647, 6.0, 0.4, 'wall', 'WallH_2'),
    box(-21.277, 3.0, -31.194, 5.4, 6.0, 0.4, 'wall', 'WallH_3'),
    box(18.524, 3.0, -31.194, 10.002, 6.0, 0.4, 'wall', 'WallH_4'),
    box(7.274, 3.0, -28.694, 12.503, 6.0, 0.4, 'wall', 'WallH_5'),
    box(32.475, 3.0, -26.194, 12.895, 6.0, 0.4, 'wall', 'WallH_6'),
    box(-33.974, 3.0, -23.694, 5.007, 6.0, 0.4, 'wall', 'WallH_7'),
    box(-26.082, 3.0, -23.694, 5.009, 6.0, 0.4, 'wall', 'WallH_8'),
    box(-3.766, 3.0, -23.694, 10.377, 6.0, 0.4, 'wall', 'WallH_9'),
    box(11.223, 3.0, -25.953, 5.4, 6.0, 0.4, 'wall', 'WallH_10'),
    box(-35.019, 3.0, -21.194, 7.124, 6.0, 0.4, 'wall', 'WallH_12'),
    box(-7.533, 3.0, -21.194, 22.149, 6.0, 0.4, 'wall', 'WallH_13'),
    box(16.216, 3.0, -21.194, 4.614, 6.0, 0.4, 'wall', 'WallH_14'),
    box(22.672, 3.0, -21.194, 2.502, 6.0, 0.4, 'wall', 'WallH_15'),
    box(-2.721, 3.0, -18.694, 12.513, 6.0, 0.4, 'wall', 'WallH_16'),
    box(7.468, 3.0, -18.694, 2.108, 6.0, 0.4, 'wall', 'WallH_17'),
    box(-37.527, 3.0, -16.194, 2.9, 6.0, 0.4, 'wall', 'WallH_18'),
    box(-27.527, 3.0, -16.194, 2.9, 6.0, 0.4, 'wall', 'WallH_19'),
    box(13.527, 3.0, -16.194, 10.008, 6.0, 0.4, 'wall', 'WallH_20'),
    box(23.726, 3.0, -16.194, 4.611, 6.0, 0.4, 'wall', 'WallH_21'),
    box(-18.721, 3.0, -13.694, 15.513, 6.0, 0.4, 'wall', 'WallH_22'),
    box(10.345, 3.0, -13.694, 3.644, 6.0, 0.4, 'wall', 'WallH_23'),
    box(28.827, 3.0, -13.694, 9.82, 6.0, 0.4, 'wall', 'WallH_24'),
    box(37.212, 3.0, -13.694, 2.726, 6.0, 0.4, 'wall', 'WallH_25'),
    box(-21.885, 3.0, -8.703, 14.714, 6.0, 0.4, 'wall', 'WallH_26'),
    box(-9.77, 3.0, -11.445, 2.373, 6.0, 0.4, 'wall', 'WallH_27'),
    box(-35.027, 3.0, -8.694, 2.101, 6.0, 0.4, 'wall', 'WallH_28'),
    box(15.739, 3.0, -2.548, 6.367, 6.0, 0.4, 'wall', 'WallH_30'),
    box(22.473, 3.0, -2.456, 2.9, 6.0, 0.4, 'wall', 'WallH_31'),
    box(37.193, 3.0, -6.194, 2.676, 6.0, 0.4, 'wall', 'WallH_32'),
    box(32.662, 3.0, 1.306, 2.522, 6.0, 0.4, 'wall', 'WallH_33'),
    box(37.473, 3.0, 2.595, 2.9, 6.0, 2.978, 'wall', 'WallH_34'),
    box(-24.173, 3.0, 3.806, 10.146, 6.0, 0.4, 'wall', 'WallH_35'),
    box(-11.492, 3.0, 3.806, 9.971, 6.0, 0.4, 'wall', 'WallH_36'),
    box(7.733, 3.0, 3.806, 21.682, 6.0, 0.4, 'wall', 'WallH_37'),
    box(27.672, 3.0, 3.806, 12.502, 6.0, 0.4, 'wall', 'WallH_38'),
    box(-30.904, 3.0, 6.306, 6.147, 6.0, 0.4, 'wall', 'WallH_39'),
    box(-17.594, 3.0, 6.306, 2.242, 6.0, 0.4, 'wall', 'WallH_40'),
    box(-17.445, 3.0, 8.804, 1.943, 6.0, 0.4, 'wall', 'WallH_41'),
    box(-21.193, 3.0, 13.806, 4.778, 6.0, 0.4, 'wall', 'WallH_44'),
    box(-12.719, 3.0, 18.806, 7.516, 6.0, 0.4, 'wall', 'WallH_45'),
    box(-0.012, 3.0, 18.806, 12.134, 6.0, 0.4, 'wall', 'WallH_46'),
    box(13.919, 3.0, 18.806, 10.007, 6.0, 0.4, 'wall', 'WallH_47'),
    box(23.901, 3.0, 18.806, 5.043, 6.0, 0.4, 'wall', 'WallH_48'),
    box(33.57, 3.0, 18.806, 10.094, 6.0, 0.4, 'wall', 'WallH_49'),
    box(4.12, 3.0, 21.306, 3.839, 6.0, 0.4, 'wall', 'WallH_50'),
    box(10.171, 3.0, 21.306, 2.504, 6.0, 0.4, 'wall', 'WallH_51'),
    box(23.723, 3.0, 21.306, 5.4, 6.0, 0.4, 'wall', 'WallH_52'),
    box(31.18, 3.0, 21.306, 5.314, 6.0, 0.4, 'wall', 'WallH_53'),
    box(16.223, 3.0, 23.806, 10.4, 6.0, 0.4, 'wall', 'WallH_54'),
    box(16.223, 3.0, 30.104, 10.4, 6.0, 0.4, 'wall', 'WallH_55'),
    box(-29.656, 3.0, 23.758, 3.643, 6.0, 0.4, 'wall', 'WallH_56'),
    box(-22.527, 3.0, 23.745, 2.9, 6.0, 0.4, 'wall', 'WallH_57'),
    box(-13.63, 3.0, 25.017, 10.105, 6.0, 0.4, 'wall', 'WallH_58'),
    box(27.446, 3.0, 31.832, 12.846, 6.0, 0.4, 'wall', 'WallH_61'),
    box(-19.887, 3.0, 31.306, 2.404, 6.0, 0.4, 'wall', 'WallH_62'),
    box(-2.376, 3.0, 28.817, 8.164, 6.0, 0.4, 'wall', 'WallH_63'),
    box(-19.89, 3.0, 33.806, 2.389, 6.0, 0.4, 'wall', 'WallH_64'),
    box(2.668, 3.0, 33.806, 17.509, 6.0, 0.4, 'wall', 'WallH_65'),
    box(-26.277, 3.0, 36.306, 10.4, 6.0, 0.4, 'wall', 'WallH_66'),
    box(-12.371, 3.0, 36.156, 12.627, 6.0, 0.4, 'wall', 'WallH_67'),
    box(-38.777, 3.0, -18.694, 0.4, 6.0, 5.4, 'wall', 'WallV_68'),
    box(-36.277, 3.0, -29.947, 0.4, 6.0, 12.102, 'wall', 'WallV_69'),
    box(-36.277, 3.0, -12.444, 0.4, 6.0, 7.9, 'wall', 'WallV_70'),
    box(-33.777, 3.0, -1.393, 0.4, 6.0, 15.002, 'wall', 'WallV_71'),
    box(-31.277, 3.0, -22.444, 0.4, 6.0, 2.9, 'wall', 'WallV_72'),
    box(-29.438, 3.0, -2.444, 0.4, 6.0, 12.9, 'wall', 'WallV_73'),
    box(-31.277, 3.0, 30.012, 0.4, 6.0, 12.193, 'wall', 'WallV_74'),
    box(-28.777, 3.0, -20.143, 0.4, 6.0, 7.501, 'wall', 'WallV_75'),
    box(-26.277, 3.0, -14.942, 0.4, 6.0, 2.103, 'wall', 'WallV_76'),
    box(-28.031, 3.0, 15.232, 0.4, 6.0, 17.465, 'wall', 'WallV_78'),
    box(-23.777, 3.0, -34.944, 0.4, 6.0, 2.9, 'wall', 'WallV_79'),
    box(-23.777, 3.0, -27.442, 0.4, 6.0, 7.119, 'wall', 'WallV_80'),
    box(-23.777, 3.0, 18.577, 0.4, 6.0, 9.942, 'wall', 'WallV_81'),
    box(-18.91, 3.0, 5.056, 0.4, 6.0, 2.9, 'wall', 'WallV_82'),
    box(-18.615, 3.0, 11.306, 0.4, 6.0, 5.4, 'wall', 'WallV_83'),
    box(-21.277, 3.0, 27.698, 0.4, 6.0, 7.616, 'wall', 'WallV_84'),
    box(-21.277, 3.0, 35.056, 0.4, 6.0, 2.9, 'wall', 'WallV_85'),
    box(-18.777, 3.0, -26.194, 0.4, 6.0, 10.4, 'wall', 'WallV_86'),
    box(-8.761, 3.0, -28.889, 0.4, 6.0, 10.009, 'wall', 'WallV_87'),
    box(-16.277, 3.0, 5.24, 0.4, 6.0, 2.532, 'wall', 'WallV_88'),
    box(-16.277, 3.0, 13.806, 0.4, 6.0, 10.4, 'wall', 'WallV_89'),
    box(-18.485, 3.0, 28.347, 0.4, 6.0, 6.319, 'wall', 'WallV_90'),
    box(-18.489, 3.0, 34.782, 0.4, 6.0, 2.352, 'wall', 'WallV_91'),
    box(-8.777, 3.0, -15.272, 0.4, 6.0, 7.244, 'wall', 'WallV_92'),
    box(-8.777, 3.0, 21.709, 0.4, 6.0, 6.206, 'wall', 'WallV_93'),
    box(-6.277, 3.0, 20.108, 0.4, 6.0, 3.004, 'wall', 'WallV_95'),
    box(-3.227, 3.0, -1.054, 0.4, 6.0, 10.12, 'wall', 'WallV_96'),
    box(-6.264, 3.0, 25.106, 0.4, 6.0, 7.012, 'wall', 'WallV_97'),
    box(-6.263, 3.0, 34.787, 0.4, 6.0, 2.362, 'wall', 'WallV_98'),
    box(1.223, 3.0, -26.194, 0.4, 6.0, 5.4, 'wall', 'WallV_99'),
    box(3.723, 3.0, -19.944, 0.4, 6.0, 2.9, 'wall', 'WallV_100'),
    box(6.223, 3.0, -19.944, 0.4, 6.0, 2.9, 'wall', 'WallV_102'),
    box(6.223, 3.0, 20.056, 0.4, 6.0, 2.9, 'wall', 'WallV_103'),
    box(8.723, 3.0, -23.372, 0.4, 6.0, 4.755, 'wall', 'WallV_105'),
    box(8.723, 3.0, -17.652, 0.4, 6.0, 2.484, 'wall', 'WallV_106'),
    box(8.723, 3.0, -11.312, 0.4, 6.0, 5.164, 'wall', 'WallV_107'),
    box(8.723, 3.0, 20.056, 0.4, 6.0, 2.9, 'wall', 'WallV_108'),
    box(11.223, 3.0, 22.555, 0.4, 6.0, 2.115, 'wall', 'WallV_109'),
    box(11.223, 3.0, 31.951, 0.4, 6.0, 3.321, 'wall', 'WallV_110'),
    box(13.723, 3.0, -29.745, 0.4, 6.0, 2.503, 'wall', 'WallV_111'),
    box(12.356, 3.0, -8.121, 0.4, 6.0, 11.546, 'wall', 'WallV_112'),
    box(18.723, 3.0, -18.694, 0.4, 6.0, 5.4, 'wall', 'WallV_113'),
    box(18.723, 3.0, 0.822, 0.4, 6.0, 6.369, 'wall', 'WallV_114'),
    box(18.723, 3.0, 14.855, 0.4, 6.0, 7.498, 'wall', 'WallV_115'),
    box(21.223, 3.0, -18.694, 0.4, 6.0, 5.4, 'wall', 'WallV_116'),
    box(21.223, 3.0, 3.36, 0.4, 6.0, 11.293, 'wall', 'WallV_117'),
    box(21.223, 3.0, 22.559, 0.4, 6.0, 2.107, 'wall', 'WallV_118'),
    box(21.223, 3.0, 30.961, 0.4, 6.0, 1.348, 'wall', 'WallV_119'),
    box(23.723, 3.0, -26.36, 0.4, 6.0, 10.067, 'wall', 'WallV_120'),
    box(23.723, 3.0, -8.265, 0.4, 6.0, 11.257, 'wall', 'WallV_122'),
    box(21.166, 3.0, 15.056, 0.4, 6.0, 7.9, 'wall', 'WallV_124'),
    box(26.223, 3.0, -21.014, 0.4, 6.0, 10.04, 'wall', 'WallV_125'),
    box(26.223, 3.0, 20.056, 0.4, 6.0, 2.9, 'wall', 'WallV_126'),
    box(28.723, 3.0, 20.056, 0.4, 6.0, 2.9, 'wall', 'WallV_127'),
    box(31.223, 3.0, -6.009, 0.4, 6.0, 15.03, 'wall', 'WallV_128'),
    box(34.062, 3.0, 26.567, 0.4, 6.0, 10.921, 'wall', 'WallV_129'),
    box(38.723, 3.0, -9.94, 0.4, 6.0, 7.103, 'wall', 'WallV_130'),
    box(33.723, 3.0, 2.559, 0.4, 6.0, 2.117, 'wall', 'WallV_131'),
    box(38.809, 3.0, 11.533, 0.4, 6.0, 14.947, 'wall', 'WallV_132'),
    box(38.723, 3.0, -19.944, 0.4, 6.0, 12.9, 'wall', 'WallV_133'),
    box(38.723, 3.0, -2.635, 0.4, 6.0, 7.518, 'wall', 'WallV_134'),
    box(2.144, 3.009, 23.648, 0.903, 6.0, 4.292, 'wall', 'WallV_135'),
    box(19.947, 3.0, 11.317, 2.061, 6.0, 0.4, 'wall', 'WallH_60'),
    box(-14.739, 3.0, -2.506, 0.4, 6.0, 12.236, 'wall', 'WallV_101'),
    box(-10.75, 3.0, -7.618, 0.4, 6.0, 2.237, 'wall', 'WallV_104'),
    box(-6.982, 3.0, -6.314, 7.94, 6.0, 0.4, 'wall', 'WallH_42'),
    box(-0.813, 3.0, -8.541, 19.471, 6.0, 0.4, 'wall', 'WallH_29'),
    box(-10.766, 3.0, -12.763, 0.4, 6.0, 2.231, 'wall', 'WallV_94'),
    box(7.273, 3.0, -21.194, 2.501, 6.0, 0.4, 'wall', 'WallH_43'),
    box(13.715, 3.0, -23.372, 0.4, 6.0, 4.755, 'wall', 'WallV_121'),
    box(2.141, 3.009, 29.62, 0.909, 6.0, 3.632, 'wall', 'WallV_136'),
    box(1.811, 3.009, 26.821, 0.249, 6.0, 2.053, 'wall', 'WallV_137'),
    box(2.265, 4.503, 26.798, 0.662, 3.01, 2.01, 'wall', 'WallV_138'),
    box(-30.027, 6.2, -29.944, 12.5, 0.4, 12.5, 'ceiling', 'Ceil_1'),
    box(-21.253, 6.2, -32.444, 5.049, 0.4, 2.5, 'ceiling', 'Ceil_2'),
    box(-13.772, 6.2, -27.44, 10.011, 0.4, 12.493, 'ceiling', 'Ceil_3'),
    box(18.723, 6.2, -26.194, 10.0, 0.4, 10.0, 'ceiling', 'Ceil_4'),
    box(4.973, 6.2, -24.944, 7.5, 0.4, 7.5, 'ceiling', 'Ceil_5'),
    box(32.462, 6.2, -24.944, 12.521, 0.4, 2.5, 'ceiling', 'Ceil_6'),
    box(-30.027, 6.2, -16.194, 2.5, 0.4, 15.0, 'ceiling', 'Ceil_7'),
    box(-3.78, 6.2, -22.444, 10.005, 0.4, 2.5, 'ceiling', 'Ceil_8'),
    box(11.223, 6.2, -27.289, 5.0, 0.4, 2.725, 'ceiling', 'Ceil_9'),
    box(32.473, 6.2, -18.694, 12.5, 0.4, 10.0, 'ceiling', 'Ceil_10'),
    box(-35.027, 6.2, -18.694, 7.5, 0.4, 5.0, 'ceiling', 'Ceil_11'),
    box(4.973, 6.2, -14.837, 2.5, 0.4, 12.713, 'ceiling', 'Ceil_12'),
    box(19.973, 6.2, -17.445, 2.5, 0.4, 7.499, 'ceiling', 'Ceil_13'),
    box(-2.527, 6.2, -13.634, 12.5, 0.4, 10.119, 'ceiling', 'Ceil_14'),
    box(7.473, 6.2, -13.604, 2.5, 0.4, 10.179, 'ceiling', 'Ceil_15'),
    box(-33.777, 6.2, -12.444, 5.0, 0.4, 7.5, 'ceiling', 'Ceil_16'),
    box(-27.527, 6.2, -12.444, 2.5, 0.4, 7.5, 'ceiling', 'Ceil_17'),
    box(13.723, 6.2, -14.944, 10.0, 0.4, 2.5, 'ceiling', 'Ceil_18'),
    box(23.723, 6.2, -14.944, 5.0, 0.4, 2.5, 'ceiling', 'Ceil_19'),
    box(-17.527, 6.2, -11.144, 17.5, 0.4, 5.099, 'ceiling', 'Ceil_20'),
    box(15.521, 6.2, -3.73, 6.404, 0.4, 2.357, 'ceiling', 'Ceil_21'),
    box(22.473, 6.2, -8.119, 2.5, 0.4, 11.149, 'ceiling', 'Ceil_22'),
    box(32.473, 6.2, -6.194, 2.5, 0.4, 15.0, 'ceiling', 'Ceil_23'),
    box(-4.784, 6.2, 11.301, 2.987, 0.4, 15.01, 'ceiling', 'Ceil_24'),
    box(-31.495, 6.2, -1.194, 4.564, 0.4, 15.0, 'ceiling', 'Ceil_25'),
    box(36.223, 6.2, -6.211, 5.0, 0.4, 15.034, 'ceiling', 'Ceil_26'),
    box(36.219, 6.2, 10.056, 4.992, 0.4, 17.5, 'ceiling', 'Ceil_27'),
    box(-25.085, 6.2, 5.056, 12.384, 0.4, 2.5, 'ceiling', 'Ceil_28'),
    box(-11.277, 6.2, 11.306, 10.0, 0.4, 15.0, 'ceiling', 'Ceil_29'),
    box(7.711, 6.2, 11.306, 22.023, 0.4, 15.0, 'ceiling', 'Ceil_30'),
    box(27.461, 6.2, 5.056, 12.524, 0.4, 2.5, 'ceiling', 'Ceil_31'),
    box(-22.092, 6.2, 7.556, 11.63, 0.4, 2.5, 'ceiling', 'Ceil_32'),
    box(23.71, 6.2, 12.556, 5.025, 0.4, 12.5, 'ceiling', 'Ceil_33'),
    box(-23.289, 6.2, 11.306, 9.253, 0.4, 5.0, 'ceiling', 'Ceil_34'),
    box(-25.845, 6.2, 25.056, 4.135, 0.4, 22.5, 'ceiling', 'Ceil_36'),
    box(29.973, 6.2, 17.556, 7.5, 0.4, 2.5, 'ceiling', 'Ceil_37'),
    box(-7.527, 6.2, 27.506, 2.5, 0.4, 17.4, 'ceiling', 'Ceil_38'),
    box(7.473, 6.2, 23.806, 2.5, 0.4, 10.0, 'ceiling', 'Ceil_39'),
    box(27.473, 6.2, 25.346, 2.5, 0.4, 13.079, 'ceiling', 'Ceil_40'),
    box(4.105, 6.2, 25.052, 4.236, 0.4, 7.492, 'ceiling', 'Ceil_41'),
    box(9.973, 6.2, 25.056, 2.5, 0.4, 7.5, 'ceiling', 'Ceil_42'),
    box(23.723, 6.2, 26.583, 5.0, 0.4, 10.555, 'ceiling', 'Ceil_43'),
    box(31.381, 6.2, 26.606, 5.343, 0.4, 10.599, 'ceiling', 'Ceil_44'),
    box(16.223, 6.2, 27.012, 10.0, 0.4, 6.196, 'ceiling', 'Ceil_45'),
    box(-29.581, 6.2, 30.081, 3.393, 0.4, 12.45, 'ceiling', 'Ceil_46'),
    box(-22.527, 6.2, 30.082, 2.5, 0.4, 12.449, 'ceiling', 'Ceil_47'),
    box(-13.645, 6.2, 30.623, 9.736, 0.4, 11.203, 'ceiling', 'Ceil_48'),
    box(-19.891, 6.2, 32.556, 2.772, 0.4, 2.5, 'ceiling', 'Ceil_50'),
    box(2.452, 6.2, 31.291, 17.505, 0.4, 5.031, 'ceiling', 'Ceil_51'),
    box(19.973, 6.2, 3.201, 2.5, 0.4, 16.211, 'ceiling', 'Ceil_35'),
    box(13.645, 6.2, -9.288, 2.651, 0.4, 8.811, 'ceiling', 'Ceil_53'),
    box(-9.041, 6.2, -1.234, 11.526, 0.4, 10.055, 'ceiling', 'Ceil_49'),
    box(-12.712, 6.2, -7.441, 4.185, 0.4, 2.339, 'ceiling', 'Ceil_52'),
    box(25.831, 3.0, 5.857, 0.8, 6.0, 0.8, 'pillar', 'CloisterPillar_1'),
    box(29.945, 3.0, 5.791, 0.8, 6.0, 0.8, 'pillar', 'CloisterPillar_2'),
    box(34.167, 3.0, 5.822, 0.8, 6.0, 0.8, 'pillar', 'CloisterPillar_3'),
    box(25.784, 3.0, 11.47, 0.8, 6.0, 0.8, 'pillar', 'CloisterPillar_4'),
    box(34.14, 3.0, 11.555, 0.8, 6.0, 0.8, 'pillar', 'CloisterPillar_5'),
    box(25.767, 3.0, 16.728, 0.8, 6.0, 0.8, 'pillar', 'CloisterPillar_6'),
    box(29.984, 3.0, 16.736, 0.8, 6.0, 0.8, 'pillar', 'CloisterPillar_7'),
    box(34.183, 3.0, 16.758, 0.8, 6.0, 0.8, 'pillar', 'CloisterPillar_8'),
    box(-13.777, 0.4, 12.556, 5.0, 0.8, 7.5, 'platform', 'ChurchAltar'),
    box(-10.777, 0.2, 12.556, 1.0, 0.4, 7.5, 'platform', 'ChurchAltarStepLow'),
    box(-5.777, 1.275, -12.131, 1.461, 2.545, 0.429, 'prop', 'LibraryBookshelf_1', 1.5708),
    box(-24.34, 0.6, 4.652, 1.2, 1.2, 1.2, 'prop', 'MetalCrate_1'),
    box(-19.34, 0.45, 9.04, 0.9, 0.9, 0.9, 'prop', 'StorageCrate_2'),
    box(-27.431, 0.5, 9.306, 0.8, 0.8, 0.8, 'prop', 'StorageCrate_3'),
    box(-24.586, 0.6, 14.219, 1.2, 1.2, 1.2, 'prop', 'MetalCrate_2'),
    box(-27.426, 0.4, 12.806, 0.8, 0.8, 0.8, 'prop', 'StorageCrate_5'),
    box(-27.217, 0.6, 20.636, 1.2, 1.2, 1.2, 'prop', 'MetalCrate_3'),
    box(-28.26, 0.4, 24.374, 0.8, 0.8, 0.8, 'prop', 'StorageCrate_7'),
    box(-21.893, 0.4, 30.063, 0.8, 0.8, 0.8, 'prop', 'StorageCrate_8'),
    box(-30.66, 0.4, 32.172, 0.8, 0.8, 0.8, 'prop', 'StorageCrate_9'),
    box(-5.777, 1.275, -17.758, 1.461, 2.545, 0.429, 'prop', 'LibraryBookshelf_2', 1.5708),
    box(-2.269, 1.275, -17.758, 1.461, 2.545, 0.429, 'prop', 'LibraryBookshelf_3', 1.5708),
    box(2.214, 1.275, -17.758, 1.461, 2.545, 0.429, 'prop', 'LibraryBookshelf_4', 1.5708),
    box(-5.777, 1.275, -16.292, 1.461, 2.545, 0.429, 'prop', 'LibraryBookshelf_5', 1.5708),
    box(-2.295, 1.275, -10.957, 1.461, 2.545, 0.429, 'prop', 'LibraryBookshelf_6', 1.5708),
    box(1.931, 1.275, -9.485, 1.461, 2.545, 0.429, 'prop', 'LibraryBookshelf_7', 1.5708),
    box(-6.93, 1.275, -1.191, 1.461, 2.545, 0.429, 'prop', 'LibraryBookshelf_8'),
    box(-11.342, 1.331, 2.417, 0.9, 0.9, 0.9, 'prop', 'StorageCrate_10', 0.7854),
    box(-11.336, 0.45, 2.413, 0.9, 0.9, 0.9, 'prop', 'StorageCrate_11', 0.7854),
    box(11.644, 0.6, 29.292, 1.2, 1.2, 1.2, 'prop', 'MetalCrate_5'),
    box(12.657, 0.4, 29.519, 0.8, 0.8, 0.8, 'prop', 'StorageCrate_13'),
    box(5.808, 0.6, 22.108, 1.2, 1.2, 1.2, 'prop', 'MetalCrate_4'),
    box(6.81, 0.4, 21.986, 0.8, 0.8, 0.8, 'prop', 'StorageCrate_15'),
    box(33.086, 1.275, 27.273, 1.461, 2.545, 0.429, 'prop', 'LibraryBookshelf_9'),
    box(33.086, 1.275, 29.443, 1.461, 2.545, 0.429, 'prop', 'LibraryBookshelf_10'),
    box(33.086, 1.275, 24.693, 1.461, 2.545, 0.429, 'prop', 'LibraryBookshelf_11'),
    box(18.896, 0.6, 24.617, 1.2, 1.2, 1.2, 'prop', 'MetalCrate_6'),
    box(18.425, 0.4, -9.16, 5.0, 0.8, 5.0, 'platform', 'ChapelAltar'),
    box(15.425, 0.2, -9.16, 1.0, 0.4, 7.0, 'platform', 'ChapelAltarStepLow'),
    box(21.439, 0.2, -9.165, 1.0, 0.4, 7.0, 'platform', 'ChapelAltarStepLow2'),
    box(18.412, 0.2, -6.162, 5.031, 0.4, 1.0, 'platform', 'ChapelAltarStepLow3'),
    box(18.408, 0.2, -12.163, 5.039, 0.4, 1.0, 'platform', 'ChapelAltarStepLow4'),
    box(-8.4, 1.275, -1.191, 1.461, 2.545, 0.429, 'prop', 'LibraryBookshelf_12'),
    box(-9.871, 1.275, -1.191, 1.461, 2.545, 0.429, 'prop', 'LibraryBookshelf_13'),
    box(-9.871, 1.275, -3.832, 1.461, 2.545, 0.429, 'prop', 'LibraryBookshelf_14'),
    box(-8.396, 1.275, -3.832, 1.461, 2.545, 0.429, 'prop', 'LibraryBookshelf_15'),
    box(-6.927, 1.275, -3.832, 1.461, 2.545, 0.429, 'prop', 'LibraryBookshelf_16'),
    box(-2.295, 1.275, -9.49, 1.461, 2.545, 0.429, 'prop', 'LibraryBookshelf_17', 1.5708),
    box(1.931, 1.275, -10.962, 1.461, 2.545, 0.429, 'prop', 'LibraryBookshelf_18', 1.5708),
    box(6.02, 1.275, -12.549, 1.461, 2.545, 0.429, 'prop', 'LibraryBookshelf_19', 1.5708),
    box(6.02, 1.275, -14.009, 1.461, 2.545, 0.429, 'prop', 'LibraryBookshelf_20', 1.5708),
    box(6.02, 1.275, -15.46, 1.461, 2.545, 0.429, 'prop', 'LibraryBookshelf_21', 1.5708),
    box(2.707, 0.406, 26.822, 0.99, 0.82, 0.94, 'prop', 'Cauldron'),
    box(29.182, 0.272, 30.373, 2.78, 0.53, 0.53, 'prop', 'Bench2'),
    box(29.182, 0.272, 28.158, 2.78, 0.53, 0.53, 'prop', 'Bench3'),
    box(29.085, 0.265, 29.246, 2.78, 0.53, 0.53, 'prop', 'Workbench2'),
    box(2.968, 0.724, 23.573, 0.71, 0.41, 0.57, 'prop', 'FarmCrate_Carrot'),
    box(2.965, 0.272, 23.013, 2.78, 0.53, 0.53, 'prop', 'Bench4', 1.5708),
    box(2.542, 1.5, 26.798, 2.0, 3.0, 0.06, 'prop', 'Wall_Arch2', 1.5708),
    box(1.945, 1.5, 26.798, 2.0, 3.0, 0.06, 'prop', 'Wall_Arch3', 1.5708),
    box(-17.739, 0.852, -29.346, 1.7, 1.62, 1.76, 'prop', 'Cage_Small2'),
    box(-17.974, 0.045, -28.016, 1.07, 0.09, 0.91, 'prop', 'Chain_Coil2'),
    box(-17.755, 0.852, -25.192, 1.7, 1.62, 1.76, 'prop', 'Cage_Small3'),
    box(1.281, 6.304, 4.001, 0.2, 0.74, 0.77, 'prop', 'Roof_Support22'),
    box(5.907, 6.304, 3.998, 0.2, 0.74, 0.77, 'prop', 'Roof_Support23'),
    box(10.411, 6.304, 3.986, 0.2, 0.74, 0.77, 'prop', 'Roof_Support24'),
    box(14.887, 6.304, 4.006, 0.2, 0.74, 0.77, 'prop', 'Roof_Support25'),
    box(-11.196, 6.304, 4.023, 0.2, 0.74, 0.77, 'prop', 'Roof_Support26'),
    box(-11.196, 6.304, 18.608, 0.2, 0.74, 0.77, 'prop', 'Roof_Support27', -3.14159),
    box(0.987, 6.304, 18.608, 0.2, 0.74, 0.77, 'prop', 'Roof_Support28', -3.14159),
    box(10.655, 6.304, 18.608, 0.2, 0.74, 0.77, 'prop', 'Roof_Support29', -3.14159),
    box(15.141, 6.304, 18.608, 0.2, 0.74, 0.77, 'prop', 'Roof_Support30', -3.14159),
    box(5.964, 6.304, 18.608, 0.2, 0.74, 0.77, 'prop', 'Roof_Support31', -3.14159),
  ],
  torches: [
    ...valhallaTorches(),
    { pos: v3(-24.226, 2.986, -29.787), yaw: 1.5708 },
    { pos: v3(-30.18, 2.986, -35.725), yaw: -3.1416 },
    { pos: v3(-35.937, 2.986, -30.185), yaw: -1.5708 },
    { pos: v3(-32.855, 2.986, -23.978), yaw: -0.0 },
    { pos: v3(-26.827, 2.986, -23.978), yaw: -0.0 },
    { pos: v3(-19.994, 2.986, -33.372), yaw: -3.1416 },
    { pos: v3(-16.557, 2.986, -21.501), yaw: -0.0 },
    { pos: v3(-29.017, 2.986, -18.843), yaw: 1.5708 },
    { pos: v3(-9.011, 2.986, -28.85), yaw: 1.5708 },
    { pos: v3(-7.758, 2.986, -23.436), yaw: -3.1416 },
    { pos: v3(-3.352, 2.986, -21.501), yaw: -0.0 },
    { pos: v3(1.537, 2.986, -26.504), yaw: -1.5708 },
    { pos: v3(8.42, 2.986, -21.51), yaw: 1.5708 },
    { pos: v3(22.004, 2.986, -21.51), yaw: -0.0 },
    { pos: v3(23.184, 2.986, -28.916), yaw: 1.5708 },
    { pos: v3(13.993, 2.986, -28.916), yaw: -1.5708 },
    { pos: v3(19.026, 2.986, -18.355), yaw: -1.5708 },
    { pos: v3(11.624, 2.986, -15.979), yaw: -3.1416 },
    { pos: v3(20.868, 3.307, 1.221), yaw: 1.5708 },
    { pos: v3(23.383, 3.307, -12.45), yaw: 1.5708 },
    { pos: v3(26.508, 2.986, -17.187), yaw: -1.5708 },
    { pos: v3(30.106, 2.986, -25.415), yaw: -3.1416 },
    { pos: v3(34.808, 2.986, -25.415), yaw: -3.1416 },
    { pos: v3(38.318, 2.986, -15.541), yaw: 1.5708 },
    { pos: v3(38.421, 2.986, -10.136), yaw: 1.5708 },
    { pos: v3(32.888, 2.986, 1.063), yaw: -0.0 },
    { pos: v3(37.147, 2.986, 1.063), yaw: 1.5708 },
    { pos: v3(31.129, 2.986, 21.838), yaw: -3.1416 },
    { pos: v3(24.026, 2.986, 21.838), yaw: -3.1416 },
    { pos: v3(30.389, 2.986, 30.889), yaw: -0.0 },
    { pos: v3(16.779, 2.986, 24.089), yaw: -3.1416 },
    { pos: v3(10.026, 2.986, 21.644), yaw: -3.1416 },
    { pos: v3(4.844, 2.986, 21.644), yaw: -3.1416 },
    { pos: v3(7.445, 2.986, 33.398), yaw: -0.0 },
    { pos: v3(-10.346, 2.986, 34.827), yaw: -0.0 },
    { pos: v3(-14.369, 2.986, 35.206), yaw: -0.0 },
    { pos: v3(-9.972, 2.986, 25.297), yaw: -3.1416 },
    { pos: v3(-14.853, 2.986, 25.373), yaw: -3.1416 },
    { pos: v3(-18.002, 2.986, 31.571), yaw: -1.5708 },
    { pos: v3(-21.714, 2.986, 30.531), yaw: 1.5708 },
    { pos: v3(-30.625, 2.986, 28.915), yaw: -1.5708 },
    { pos: v3(-27.768, 2.986, 17.506), yaw: -1.5708 },
    { pos: v3(-19.199, 2.986, 11.004), yaw: 1.5708 },
    { pos: v3(-27.757, 2.986, 11.004), yaw: -1.5708 },
    { pos: v3(-22.179, 2.986, 4.03), yaw: -3.1416 },
    { pos: v3(-15.347, 2.986, 10.241), yaw: -1.5708 },
    { pos: v3(-15.347, 2.986, 14.712), yaw: -1.5708 },
    { pos: v3(13.811, 2.986, 18.476), yaw: -0.0 },
    { pos: v3(0.457, 2.986, 18.435), yaw: -0.0 },
    { pos: v3(7.612, 2.986, 4.082), yaw: -3.1416 },
    { pos: v3(-33.417, 2.972, 4.001), yaw: -1.5708 },
    { pos: v3(-29.693, 2.986, -3.302), yaw: 1.5708 },
    { pos: v3(-35.922, 2.986, -12.197), yaw: -1.5708 },
    { pos: v3(-38.312, 2.986, -18.935), yaw: -1.5708 },
    { pos: v3(-21.337, 2.986, -8.969), yaw: -0.0 },
    { pos: v3(-3.471, 2.986, 1.288), yaw: 1.5708 },
    { pos: v3(-4.479, 2.986, -8.789), yaw: -0.0 },
    { pos: v3(4.065, 2.986, -8.815), yaw: -0.0 },
    { pos: v3(3.138, 2.986, -18.366), yaw: -3.1416 },
    { pos: v3(-4.173, 2.986, -18.366), yaw: -3.1416 },
    { pos: v3(-14.226, 2.986, -2.701), yaw: -1.5708 },
  ],
  // Open to the sky. Cool, steady, and outside the torch budget.
  moonlights: [
    { pos: v3(30.236, 7.532, 10.242), yaw: 0 },
    { pos: v3(18.694, 7.532, -9.09), yaw: 0 },
  ],
  areas: [
    { room: 'ChaosShrine', kind: 'room', label: 'Floor_ChaosShrine', pos: v3(-30.049, -0.25, -29.995), size: v3(12.314, 0.5, 12.335) },
    { room: 'ChaosShrine', kind: 'doorway', label: 'Floor_ChaosShrine_Doorway', pos: v3(-30.012, -0.25, -22.438), size: v3(2.313, 0.5, 2.786) },
    { room: 'ChaosShrine', kind: 'corridor', label: 'Floor_ChaosShrine_Corridor', pos: v3(-21.231, -0.25, -32.488), size: v3(5.338, 0.5, 2.359) },
    { room: 'ChaosPool', kind: 'room', label: 'Floor_ChaosPool01', pos: v3(-33.814, -0.25, -18.554), size: v3(9.696, 0.5, 4.987) },
    { room: 'ChaosPool', kind: 'room', label: 'Floor_ChaosPool02', pos: v3(-31.344, -0.25, -12.449), size: v3(9.966, 0.5, 7.242) },
    { room: 'ChaosPool', kind: 'gallery', label: 'Floor_ChaosPool_Gallery', pos: v3(-31.607, -0.25, -1.344), size: v3(3.954, 0.5, 14.992) },
    { room: 'Dungeon', kind: 'room', label: 'Floor_Dungeon', pos: v3(-13.756, -0.25, -27.414), size: v3(9.657, 0.5, 12.272) },
    { room: 'Dungeon', kind: 'corridor', label: 'Floor_Dungeon_Corridor', pos: v3(-3.763, -0.25, -22.473), size: v3(10.354, 0.5, 2.383) },
    { room: 'Library', kind: 'gallery', label: 'Floor_Library_Gallery', pos: v3(-18.652, -0.25, -11.242), size: v3(15.434, 0.5, 4.796) },
    { room: 'Library', kind: 'doorway', label: 'Floor_Library_Doorway_01', pos: v3(-9.777, -0.25, -10.032), size: v3(2.346, 0.5, 2.591) },
    { room: 'Library', kind: 'doorway', label: 'Floor_Library_Doorway_02', pos: v3(4.96, -0.25, -19.935), size: v3(2.247, 0.5, 2.809) },
    { room: 'Library', kind: 'room', label: 'Floor_Library', pos: v3(-0.046, -0.25, -13.584), size: v3(17.16, 0.5, 9.903) },
    { room: 'Library', kind: 'corridor', label: 'Floor_Library_Corridor', pos: v3(10.513, -0.25, -14.935), size: v3(3.972, 0.5, 2.153) },
    { room: 'Greenhouse', kind: 'room', label: 'Floor_Greenhouse01', pos: v3(4.967, -0.25, -24.935), size: v3(7.133, 0.5, 7.213) },
    { room: 'Greenhouse', kind: 'room', label: 'Floor_Greenhouse02', pos: v3(11.211, -0.25, -27.342), size: v3(5.365, 0.5, 2.372) },
    { room: 'Greenhouse', kind: 'room', label: 'Floor_Greenhouse03', pos: v3(18.768, -0.25, -26.18), size: v3(9.796, 0.5, 9.653) },
    { room: 'Greenhouse', kind: 'corridor', label: 'Floor_Greenhouse_Corridor', pos: v3(19.971, -0.25, -18.684), size: v3(2.156, 0.5, 5.371) },
    { room: 'Tabernacle', kind: 'room', label: 'Floor_Tabernacle', pos: v3(18.037, -0.25, -9.298), size: v3(11.179, 0.5, 13.405) },
    { room: 'Tabernacle', kind: 'corridor', label: 'Floor_Tabernacle_Corridor', pos: v3(19.971, -0.25, 0.691), size: v3(2.156, 0.5, 6.647) },
    { room: 'OrderShrine', kind: 'corridor', label: 'Floor_OrderShrine_Corridor', pos: v3(25.01, -0.25, -14.935), size: v3(2.775, 0.5, 2.153) },
    { room: 'OrderShrine', kind: 'room', label: 'Floor_OrderShrine', pos: v3(32.479, -0.25, -19.975), size: v3(12.163, 0.5, 12.195) },
    { room: 'OrderShrine', kind: 'doorway', label: 'Floor_OrderShrine_Doorway', pos: v3(34.803, -0.25, -13.704), size: v3(2.192, 0.5, 0.359) },
    { room: 'OrderPool', kind: 'room', label: 'Floor_OrderPool', pos: v3(34.964, -0.25, -6.187), size: v3(7.331, 0.5, 14.69) },
    { room: 'OrderPool', kind: 'doorway', label: 'Floor_OrderPool_Doorway', pos: v3(34.962, -0.25, 1.326), size: v3(2.118, 0.5, 0.342) },
    { room: 'Cloister', kind: 'room', label: 'Floor_Cloister01', pos: v3(30.004, -0.25, 11.283), size: v3(17.318, 0.5, 14.661) },
    { room: 'Cloister', kind: 'room', label: 'Floor_Cloister02', pos: v3(34.968, -0.25, 2.736), size: v3(2.142, 0.5, 2.479) },
    { room: 'Cloister', kind: 'doorway', label: 'Floor_Cloister_Doorway_01', pos: v3(27.477, -0.25, 20.057), size: v3(2.218, 0.5, 2.938) },
    { room: 'Cloister', kind: 'doorway', label: 'Floor_Cloister_Doorway_02', pos: v3(21.189, -0.25, 10.073), size: v3(0.319, 0.5, 2.158) },
    { room: 'Chapel', kind: 'room', label: 'Floor_Chapel01', pos: v3(1.403, -0.25, 11.302), size: v3(35.069, 0.5, 14.624) },
    { room: 'Chapel', kind: 'room', label: 'Floor_Chapel02', pos: v3(19.99, -0.25, 7.566), size: v3(2.118, 0.5, 7.103) },
    { room: 'Diningroom', kind: 'room', label: 'Floor_Diningroom', pos: v3(27.646, -0.25, 26.642), size: v3(12.602, 0.5, 10.277) },
    { room: 'Kitchen', kind: 'gallery', label: 'Floor_Kitchen_Gallery', pos: v3(16.189, -0.25, 26.95), size: v3(10.333, 0.5, 5.99) },
    { room: 'Kitchen', kind: 'room', label: 'Floor_Kitchen', pos: v3(6.478, -0.25, 27.565), size: v3(9.13, 0.5, 12.237) },
    { room: 'Kitchen', kind: 'doorway', label: 'Floor_Kitchen_Doorway', pos: v3(7.462, -0.25, 20.029), size: v3(2.181, 0.5, 2.851) },
    { room: 'Study', kind: 'room', label: 'Floor_Study', pos: v3(-9.027, -0.25, -1.243), size: v3(11.347, 0.5, 9.765) },
    { room: 'Study', kind: 'corridor', label: 'Floor_Study_Corridor', pos: v3(-12.768, -0.25, -7.478), size: v3(3.65, 0.5, 2.733) },
    { room: 'Study', kind: 'doorway', label: 'Floor_Study_Doorway', pos: v3(-4.972, -0.25, 3.814), size: v3(3.131, 0.5, 0.364) },
    { room: 'Storage', kind: 'doorway', label: 'Floor_Storage_Doorway_01', pos: v3(-28.742, -0.25, 5.061), size: v3(1.828, 0.5, 2.137) },
    { room: 'Storage', kind: 'doorway', label: 'Floor_Storage_Doorway_02', pos: v3(-17.485, -0.25, 7.554), size: v3(2.736, 0.5, 2.128) },
    { room: 'Storage', kind: 'room', label: 'Floor_Storage', pos: v3(-23.33, -0.25, 8.73), size: v3(9.015, 0.5, 9.917) },
    { room: 'Storage', kind: 'gallery', label: 'Floor_Storage_Gallery', pos: v3(-25.905, -0.25, 18.815), size: v3(3.864, 0.5, 10.256) },
    { room: 'BalancePool', kind: 'room', label: 'Floor_BalancePool', pos: v3(-26.327, -0.25, 30.047), size: v3(9.75, 0.5, 12.231) },
    { room: 'BalanceShrine', kind: 'room', label: 'Floor_BalanceShrine', pos: v3(-12.355, -0.25, 30.572), size: v3(11.898, 0.5, 10.849) },
    { room: 'BalanceShrine', kind: 'gallery', label: 'Floor_BalanceShrine_Gallery', pos: v3(-2.369, -0.25, 31.325), size: v3(8.132, 0.5, 4.642) },
    { room: 'BalanceShrine', kind: 'corridor', label: 'Floor_BalanceShrine_Corridor_01', pos: v3(-7.518, -0.25, 21.892), size: v3(2.214, 0.5, 6.558) },
    { room: 'BalanceShrine', kind: 'corridor', label: 'Floor_BalanceShrine_Corridor_02', pos: v3(-19.878, -0.25, 32.547), size: v3(3.181, 0.5, 2.14) },
    { room: 'BalanceShrine', kind: 'doorway', label: 'Floor_BalanceShrine_Doorway', pos: v3(1.921, -0.25, 32.507), size: v3(0.463, 0.5, 2.217) },
  ],
};

export function makeGreatCathedral(): Arena {
  return {
    name: 'Great Cathedral',
    shrines: [
      makeShrine('chaos', v3(-30.027, 0.0, -29.944), v3(-36.895, 0.2, -19.685)),
      makeShrine('order', v3(32.473, 0.0, -19.944), v3(36.496, 0.651, -23.673)),
      makeShrine('balance', v3(-7.527, 0.0, 35.056), v3(-15.721, 0.798, 27.936)),
    ],
    pools: [
      makePool('pool_chaospool', 'Chaos Pool', v3(-32.527, 0.0, -17.337), 2.0),
      makePool('pool_library', 'Library', v3(-0.027, 0.0, -14.944), 2.0),
      makePool('pool_greenhouse', 'Greenhouse', v3(17.473, 0.0, -27.444), 2.0),
      makePool('pool_orderpool', 'Order Pool', v3(34.973, 0.0, -9.898), 2.0),
      makePool('pool_storage', 'Storage', v3(-23.674, 0.0, 8.961), 1.56),
      makePool('pool_church', 'Church', v3(1.511, 0.0, 10.981), 2.846),
      makePool('pool_kitchen', 'Kitchen', v3(24.973, 0.0, 25.056), 2.0),
      makePool('pool_balancepool', 'Balance Pool', v3(-27.527, 0.0, 32.556), 2.0),
    ],
    valhalla: [
      {
        pos: v3(0, VY, -VALHALLA_WALK / 2),
        radius: VHALFW,
        exits: [v3(0, VY, VALHALLA_WALK / 2)],
      },
    ],
    spawns: {
      order: [v3(36.496, 0.651, -23.673), v3(34.838, 0.651, -23.662), v3(36.838, 0.651, -21.662)],
      balance: [v3(-15.721, 0.798, 27.936), v3(-14.162, 0.798, 28.338), v3(-16.162, 0.798, 30.338)],
      chaos: [v3(-36.895, 0.2, -19.685), v3(-35.162, 0.2, -19.662), v3(-37.162, 0.2, -17.662)],
    },
  };
}
